<div class="role1-010000-filing-info-container">
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>
  <app-sheet-loader [visible]="isLoading" />

  <!--
    The gate lives on the CARD, not on the <form> inside it. Gating only the
    form leaves the card shell (its body padding, and on sibling sheets its
    title and section strips) painted underneath the spinner — the residual
    layout reported against role0/role1/role2. <app-sheet-loader> stays a
    SIBLING above the card so it is not removed by this same gate; see
    scripts/check-sheet-loaders.mjs.
  -->
  <p-card *ngIf="!isLoading && schema && filingForm">
    <!-- Form Content -->
    <form [formGroup]="filingForm" class="filing-form">

      <!-- Section: General Filing Information -->
      <div class="form-section">
        <div class="form-grid">

          <!-- Render ALL fields in schema order -->
          <ng-container *ngFor="let control of schema.controls">
            <!-- Only show fields that pass shouldShowField() check -->
            <div class="form-field" *ngIf="shouldShowField(control)">
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
                <!-- <span class="warning-mark" *ngIf="isEditedMode() && hasWarnings(control.qname)">*</span> -->
              </label>

              <!-- Text Input -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'text'">
                <input
                  pInputText
                  [id]="control.qname"
                  [formControlName]="control.qname"
                  [placeholder]="control | placeholder"
                  [readonly]="control.ui.readonly || false"
                  class="w-full"
                  [maxlength]="control.ui.maxLength || null"
                  (input)="control.ui.inputFilter ? applyInputFilter($event, control.ui.inputFilter, control.ui.maxLength, control.qname, control.ui.trimLeadingZeros) : null"
                  (paste)="control.ui.inputFilter ? onPasteFilter($event, control.ui.inputFilter, control.ui.maxLength, control.qname, control.ui.trimLeadingZeros) : null"
                />

                <!-- Error messages (RED) - Blocking validation errors -->
                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                  <span *ngIf="getControl(control.qname)?.errors?.['pattern']">
                    {{ getErrorMessage(control, 'pattern') }}
                  </span>
                </small>

                <!-- Warning messages (BLACK) - Non-blocking suggestions -->
                <small class="warning-message"
                       *ngIf="isEditedMode() && hasWarnings(control.qname)">
                  <span *ngFor="let warning of getFieldWarnings(control.qname)" class="warning-text">
                    <ng-container *ngFor="let part of splitWarningMessage(warning.message)">
                      <span [class.warning-asterisk]="part.isAsterisk">{{ part.text }}</span>
                    </ng-container>
                  </span>
                </small>
              </div>

              <!-- Date Picker -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'date'">
                <div [ngClass]="{'datepicker-invalid-wrapper': getControl(control.qname)?.invalid && getControl(control.qname)?.touched && !getControl(control.qname)?.disabled}">
                  <p-datepicker
                    [inputId]="control.qname"
                    [formControlName]="control.qname"
                    dateFormat="yy-mm-dd"
                    [showIcon]="true"
                    iconDisplay="input"
                    [placeholder]="control | fixedPlaceholder: 'Select a date'"
                    class="w-full"
                    [minDate]="getDateFloor(control)"
                    [keepInvalid]="!!getDateFloor(control)"
                    (onInput)="onInputChange($event)"
                    (paste)="onDatePaste($event)"
                  >
                    <!--
                      [keepInvalid] rides on the same predicate as [minDate]:
                      without it PrimeNG renders a below-floor date as an EMPTY
                      input (formatDateTime blanks anything under minDate) while
                      the control still holds it, so a typed or Phase 1
                      extracted pre-2017 end date looked deleted and its inline
                      error explained a field that appeared blank.
                    -->
                    <!--
                      Only the current financial year END date has a [minDate],
                      so this template renders solely for days below the
                      2017-01-31 floor. The tooltip is still gated on the same
                      predicate so a future disabled-date rule cannot inherit
                      this copy by accident.
                    -->
                    <!--
                      `#disabledDate`, not `pTemplate="disabledDate"`: this
                      component imports the standalone `DatePicker`, so the
                      `PrimeTemplate` directive is NOT in scope and a
                      `pTemplate` attribute would be silently ignored. The
                      `#`-form binds through DatePicker's own
                      `@ContentChild('disabledDate')`.
                    -->
                    <ng-template #disabledDate let-dayMeta>
                      <span
                        class="unsupported-date-cell"
                        [pTooltip]="unsupportedFinancialYearEndMessage"
                        tooltipPosition="top"
                        [tooltipDisabled]="!isUnsupportedFinancialYearEndDay(dayMeta)"
                        [attr.aria-label]="
                          isUnsupportedFinancialYearEndDay(dayMeta)
                            ? unsupportedFinancialYearEndMessage
                            : null
                        "
                        >{{ dayMeta.day }}</span
                      >
                    </ng-template>
                  </p-datepicker>
                </div>

                <!-- Error messages (RED) - Blocking validation errors -->
                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched && !getControl(control.qname)?.disabled">
                  <!-- Cross-field validation errors for financial year dates -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['dateRangeInvalid']">
                    {{ getControl(control.qname)?.errors?.['dateRangeInvalid'] }}
                  </span>
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['dateSequenceInvalid']">
                    {{ getControl(control.qname)?.errors?.['dateSequenceInvalid'] }}
                  </span>
                  <!-- Unsupported filing period: financial year end before 2017-01-31 -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['financialYearEndUnsupported']">
                    {{ getControl(control.qname)?.errors?.['financialYearEndUnsupported'] }}
                  </span>
                  <!-- Required field error -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['required'] && !getControl(control.qname)?.errors?.['dateRangeInvalid'] && !getControl(control.qname)?.errors?.['dateSequenceInvalid']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages (BLACK) - Non-blocking suggestions -->
                <small class="warning-message"
                       *ngIf="isEditedMode() && hasWarnings(control.qname)">
                  <span *ngFor="let warning of getFieldWarnings(control.qname)" class="warning-text">
                    <ng-container *ngFor="let part of splitWarningMessage(warning.message)">
                      <span [class.warning-asterisk]="part.isAsterisk">{{ part.text }}</span>
                    </ng-container>
                  </span>
                </small>
              </div>

              <!-- Select/Dropdown (with enumSource) -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.enumSource && !(control.ui.lockWhen && isLockActive(control))">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getDropdownOptions(control.ui.enumSource, control.qname)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="!control.ui.readonly"
                  filterPlaceholder="Search..."
                  [showClear]="true"
                ></p-select>

                <!-- Error messages (RED) - Blocking validation errors -->
                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages (BLACK) - Non-blocking suggestions -->
                <small class="warning-message"
                       *ngIf="isEditedMode() && hasWarnings(control.qname)">
                  <span *ngFor="let warning of getFieldWarnings(control.qname)" class="warning-text">
                    <ng-container *ngFor="let part of splitWarningMessage(warning.message)">
                      <span [class.warning-asterisk]="part.isAsterisk">{{ part.text }}</span>
                    </ng-container>
                  </span>
                </small>
              </div>

              <!--
                Locked text input: when a lockWhen rule is currently active,
                render a disabled text input showing the locked value instead
                of the normal select control. Takes priority over both select
                variants below so the user sees a fixed value (not a dropdown
                they cannot open).

                Developer Note (2026-04-23): Product-owner decision for the
                Basis-of-accounting field — when Origin = "Incorporated outside
                Malaysia", the only supported basis is "Malaysian Financial
                Reporting Standards"; the alternative ("Others") will not be
                supported. See `lockWhen` in role-0.form.json.
              -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.lockWhen && isLockActive(control)">
                <input
                  pInputText
                  [id]="control.qname"
                  [value]="getLockedValue(control)"
                  [disabled]="true"
                  class="w-full"
                />
              </div>

              <!-- Select/Dropdown (with static options) -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.static && !(control.ui.lockWhen && isLockActive(control))">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getStaticOptions(control.ui.static)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [showClear]="true"
                ></p-select>

                <!-- Error messages (RED) - Blocking validation errors -->
                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages (BLACK) - Non-blocking suggestions -->
                <small class="warning-message"
                       *ngIf="isEditedMode() && hasWarnings(control.qname)">
                  <span *ngFor="let warning of getFieldWarnings(control.qname)" class="warning-text">
                    <ng-container *ngFor="let part of splitWarningMessage(warning.message)">
                      <span [class.warning-asterisk]="part.isAsterisk">{{ part.text }}</span>
                    </ng-container>
                  </span>
                </small>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Action Buttons -->
      <!--
        SUBMIT GATE — must mirror the guards at the top of this component's
        onSubmit() exactly.

        Validity ALWAYS blocks. Keep `!filingForm.valid` OUTSIDE the ternary —
        folding it into the else-branch is what left Submit permanently enabled
        here (0981ced): role0 renders only in Phase 1, where it is always the
        only sheet, so the else-branch never ran.

        `isOnlyFilingSheet` waives ONLY the changes requirement, so a first
        conversion (nothing changed yet) stays submittable. The term is live
        only on the KFI submission types, which render role0 alongside other
        sheets; on the FS-* path role0 is always alone.

        The changes-term is `hasUnsubmittedChanges()` — the
        modified-vs-original verdict that also drives the pencil — and NOT
        `filingForm.pristine`. Cancel shares it, and that is the half users
        actually hit: pristine never recovers after a field is hand-reverted to
        its original value, so Cancel kept raising "Any unsaved changes will be
        lost" over a form with nothing to lose.

        DIVERGENCE FROM role1 (the only one, and it is only the WAIVER): both
        sheets gray Submit out when nothing has changed (P.O., 2026-08-17) and
        both measure "changed" with the same verdict. Only role0 waives it on
        `isOnlyFilingSheet`; role1 has no such input and must never gain one —
        the container clamps `filteredSheets` to a single sheet once the gate
        sheet reports hasErrors, which would flip the flag true exactly when
        Submit must stay dead. (Phase-1-only since 2026-08-24 — see
        SheetApplicabilityService's VALIDITY clamp — which is the phase this
        sheet renders in, so the waiver below is unaffected.) Everything else
        about the two gates must stay in step.
        See role1-010000-filing-info.component.html.
      -->
      <app-filing-action-buttons
        [submitDisabled]="!filingForm.valid || (isOnlyFilingSheet ? false : !hasUnsubmittedChanges())"
        [cancelDisabled]="!hasUnsubmittedChanges()"
        [isSubmitting]="isSubmitting"
        (submit)="onSubmit()"
        (cancel)="onCancel()"
        (delete)="onDelete()"
      >
        <!--
          Phase 1 is the only phase that mounts this sheet, and a Phase 1
          conversion always needs a fresh Phase 2 run, so the picker is never
          disabled here. Contrast role 1, which gates on hasUnsubmittedChanges().
        -->
        <app-conversion-engine-picker
          [disabled]="isEnginePickerDisabled()"
          [lastUsedEngine]="lastUsedEngine"
          (engineChange)="onEngineChange($event)"
        ></app-conversion-engine-picker>
      </app-filing-action-buttons>
    </form>
  </p-card>

  <!-- Delete conversion dialog -->
  <app-delete-conversion
    [(isArchiveDialogOpen)]="isArchiveDialogOpen"
    [conversionData]="conversionData"
    (archiveConversionCompleted)="onDeletionCompleted()"
  ></app-delete-conversion>

  <!-- Edit warning dialog -->
  <app-filing-info-confirmation-dialog
    [(visible)]="isEditWarningDialogOpen"
    title="Filing Information & Scope of Filing Confirmation"
    (back)="onEditWarningBack()"
    (convert)="onEditWarningConvert()"
  ></app-filing-info-confirmation-dialog>
</div>
